import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Catalogue() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("jewellery");
  const [editId, setEditId] = useState("");
  const [edit, setEdit] = useState({});
  const [form, setForm] = useState({
    name: "",
    category: "jewellery",
    purity: "22kt gold 916",
    default_rate: 0,
    unit: "gram",
  });

  const load = () =>
    api
      .get("/catalogue", { params: { category } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Item name is required");
    try {
      await api.post("/catalogue", { ...form, default_rate: Number(form.default_rate) || 0 });
      toast.success("Item added to catalogue");
      setForm({ ...form, name: "", default_rate: 0 });
      setCategory(form.category);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const saveRate = async (item, rate) => {
    try {
      await api.put(`/catalogue/${item.id}`, { ...item, default_rate: Number(rate) || 0 });
      toast.success("Rate updated");
      load();
    } catch (e) {
      toast.error("Could not update rate");
    }
  };

  const saveEdit = async () => {
    if (!edit.name?.trim()) return toast.error("Item name is required");
    try {
      await api.put(`/catalogue/${editId}`, {
        name: edit.name.trim(),
        category: edit.category,
        purity: edit.purity || "",
        unit: edit.unit || "gram",
        default_rate: Number(edit.default_rate) || 0,
      });
      toast.success("Item updated");
      setEditId("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update item");
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/catalogue/${item.id}`);
      toast.success("Item removed");
      load();
    } catch (e) {
      toast.error("Could not remove item");
    }
  };

  return (
    <div className="fade-up space-y-7" data-testid="catalogue-page">
      <div>
        <h1 className="text-3xl font-bold sm:text-4xl">Item Catalogue</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Reusable jewellery and bullion descriptions with default rates. Use the pencil to
          correct a name, purity or unit.
        </p>
      </div>

      <form
        onSubmit={add}
        className="grid grid-cols-1 items-end gap-4 border border-border p-5 sm:grid-cols-5"
        data-testid="add-catalogue-form"
      >
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-wider">Item name</Label>
          <Input
            className="h-10 rounded-none"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="chain women's 22kt gold 916"
            data-testid="catalogue-name-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider">Category</Label>
          <Select
            value={form.category}
            onValueChange={(v) =>
              setForm({
                ...form,
                category: v,
                unit: v === "bullion" ? "piece" : "gram",
                purity: v === "bullion" ? "9999 - 24kt gold" : "22kt gold 916",
              })
            }
          >
            <SelectTrigger className="h-10 rounded-none" data-testid="catalogue-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white">
              <SelectItem value="jewellery">Jewellery</SelectItem>
              <SelectItem value="bullion">Bullion</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider">Default rate</Label>
          <Input
            type="number"
            step="0.01"
            className="h-10 rounded-none"
            value={form.default_rate}
            onChange={(e) => setForm({ ...form, default_rate: e.target.value })}
            data-testid="catalogue-rate-input"
          />
        </div>
        <Button
          type="submit"
          className="h-10 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          data-testid="catalogue-add-button"
        >
          <Plus className="mr-1 h-4 w-4" /> Add item
        </Button>
      </form>

      <div className="flex gap-px border border-border bg-border w-fit">
        {["jewellery", "bullion"].map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`catalogue-filter-${c}`}
            className={`px-4 py-2 text-sm capitalize transition-colors duration-150 ${
              category === c ? "bg-[#0A0A0A] font-semibold text-white" : "bg-white hover:bg-[#FBF8F2]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto border border-border">
        <table className="w-full min-w-[620px] text-sm" data-testid="catalogue-table">
          <thead className="bg-[#F8F9FA]">
            <tr className="border-b border-border">
              <th className="px-3 py-2.5 text-left font-semibold">Item</th>
              <th className="px-3 py-2.5 text-left font-semibold">Purity</th>
              <th className="px-3 py-2.5 text-left font-semibold">Unit</th>
              <th className="px-3 py-2.5 text-right font-semibold">Default rate</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((it) =>
              editId === it.id ? (
                <tr key={it.id} className="bg-[#FBF8F2]" data-testid={`catalogue-row-${it.id}`}>
                  <td className="px-3 py-2">
                    <Input
                      className="h-9 rounded-none"
                      value={edit.name}
                      onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                      data-testid={`catalogue-edit-name-${it.id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      className="h-9 rounded-none"
                      value={edit.purity}
                      onChange={(e) => setEdit({ ...edit, purity: e.target.value })}
                      data-testid={`catalogue-edit-purity-${it.id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      className="h-9 w-24 rounded-none"
                      value={edit.unit}
                      onChange={(e) => setEdit({ ...edit, unit: e.target.value })}
                      data-testid={`catalogue-edit-unit-${it.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      className="ml-auto h-9 w-28 rounded-none text-right"
                      value={edit.default_rate}
                      onChange={(e) => setEdit({ ...edit, default_rate: e.target.value })}
                      data-testid={`catalogue-edit-rate-${it.id}`}
                    />
                  </td>
                  <td className="px-2">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={saveEdit}
                        className="p-1.5 text-[#B38F4E] transition-colors duration-150 hover:text-[#0A0A0A]"
                        data-testid={`catalogue-save-${it.id}`}
                        aria-label="Save item"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setEditId("")}
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-[#0A0A0A]"
                        data-testid={`catalogue-cancel-${it.id}`}
                        aria-label="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={it.id} className="hover:bg-[#FBF8F2]" data-testid={`catalogue-row-${it.id}`}>
                  <td className="px-3 py-2">{it.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{it.purity}</td>
                  <td className="px-3 py-2 text-muted-foreground">{it.unit}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      defaultValue={it.default_rate}
                      onBlur={(e) => {
                        if (Number(e.target.value) !== it.default_rate)
                          saveRate(it, e.target.value);
                      }}
                      className="ml-auto h-9 w-28 rounded-none text-right"
                      data-testid={`catalogue-rate-${it.id}`}
                    />
                  </td>
                  <td className="px-2">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => {
                          setEditId(it.id);
                          setEdit({
                            name: it.name || "",
                            category: it.category || category,
                            purity: it.purity || "",
                            unit: it.unit || "gram",
                            default_rate: it.default_rate ?? 0,
                          });
                        }}
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-[#B38F4E]"
                        data-testid={`catalogue-edit-${it.id}`}
                        aria-label="Edit item"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(it)}
                        className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                        data-testid={`catalogue-delete-${it.id}`}
                        aria-label="Delete item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
